import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import useEditorAutoGrow from './useEditorAutoGrow';

const DEFAULT_LETTERS = { T: 'T', F: 'F', N: 'N' };

const getEffectiveLetters = (letters) => ({
  T: ((letters?.T ?? '').toString().trim().toUpperCase().slice(0, 1)) || 'T',
  F: ((letters?.F ?? '').toString().trim().toUpperCase().slice(0, 1)) || 'F',
  N: ((letters?.N ?? '').toString().trim().toUpperCase().slice(0, 1)) || 'N',
});

const normalizeLetter = (v, opts = {}) => {
  const s = (v || '').toString().trim().toUpperCase();
  if (!s) return '';
  const allowNoInfo = typeof opts === 'boolean' ? opts : !!opts.allowNoInfo;
  const letters = typeof opts === 'boolean' ? DEFAULT_LETTERS : getEffectiveLetters(opts.letters);
  const roles = allowNoInfo ? ['T', 'F', 'N'] : ['T', 'F'];
  for (const role of roles) {
    if (letters[role] === s) return role;
  }
  // Answers are stored as roles (T/F/N) after the first normalization pass,
  // so re-checking a stored value must accept the role letter itself even
  // when the admin remapped `letters` (e.g. {T:'S', F:'C'}) — otherwise the
  // teacher's Check-All silently skips the element because allFilled reads false.
  if (roles.includes(s)) return s;
  return '';
};

const TrueFalseElement = ({
  element,
  isEditor = false,
  studentView = false,
  answers: externalAnswers = null,
  showResult = false,
  perStatementResults = null,
  onAnswersChange,
  onCheck,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    question = '',
    statements = [],
    allowNoInfo = false,
    audioUrl = '',
    letters: lettersRaw,
    fontFamily = 'Inter',
    fontSize = 18,
    textColor = '#1f2937',
    letterColor,
    buttonColor = '#2A4D25',
    boxColor = '#ffffff',
    boxBorderColor = '#2A4D25',
    actionBarColor = '#f9fafb',
  } = content || {};
  const effectiveLetterColor = letterColor ?? textColor;
  const letters = getEffectiveLetters(lettersRaw);

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const [answers, setAnswers] = useState(externalAnswers || {});
  useEffect(() => {
    if (externalAnswers) setAnswers(externalAnswers);
  }, [externalAnswers]);

  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: statements.length,
    onAutoGrow,
  });

  const updateAnswers = (next) => {
    setAnswers(next);
    onAnswersChange?.(next);
  };

  const handleInputChange = (statementId, rawValue) => {
    if (disabled || showResult || isEditor) return;
    const role = normalizeLetter(rawValue, { letters: lettersRaw, allowNoInfo });
    const next = { ...answers, [statementId]: role };
    updateAnswers(next);
  };

  const handleCheck = () => onCheck?.(answers);

  const allFilled = statements.length > 0
    && statements.every((s) => !!(answers[s.id] || '').trim());

  const allCorrect = perStatementResults
    && Object.keys(perStatementResults).length > 0
    && Object.values(perStatementResults).every((v) => v === true);

  const hideChrome = !isEditor;

  const boxSize = Math.round(fontSize * 2);

  const renderBox = (statementId, correctAnswer) => {
    const value = answers[statementId] || '';
    const perResult = showResult && perStatementResults ? perStatementResults[statementId] : null;

    let bg = boxColor;
    let border = boxBorderColor;
    let color = effectiveLetterColor;
    if (perResult === true) {
      bg = '#dcfce7';
      border = '#22c55e';
      color = '#166534';
    } else if (perResult === false) {
      bg = '#fee2e2';
      border = '#ef4444';
      color = '#991b1b';
    }

    const displayValue = isEditor
      ? (letters[correctAnswer] || '')
      : (value ? (letters[value] || '') : '');

    return (
      <input
        type="text"
        value={displayValue}
        onChange={(e) => handleInputChange(statementId, e.target.value)}
        disabled={isEditor || disabled || showResult}
        maxLength={1}
        className="flex-none rounded-md border-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors text-center uppercase font-semibold"
        style={{
          width: `${boxSize}px`,
          height: `${boxSize}px`,
          backgroundColor: bg,
          borderColor: border,
          color,
          fontFamily,
          fontSize: `${Math.round(fontSize * 1.1)}px`,
        }}
      />
    );
  };

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        borderColor: showResult && !isEditor
          ? (allCorrect ? '#22c55e' : '#ef4444')
          : '#e5e7eb',
      }}
    >
      <div ref={scrollRef} className="flex-1 min-h-0 min-w-0 p-5 overflow-y-auto overflow-x-hidden">
        {question && (
          <div className="mb-3">
            <p
              className="font-medium"
              style={{
                fontSize: `${fontSize}px`,
                fontFamily,
                color: textColor,
                lineHeight: 1.3,
              }}
            >
              {question}
            </p>
          </div>
        )}

        {audioUrl && (
          <div className="mb-3">
            <audio src={resolveMediaUrl(audioUrl)} controls className="w-full" />
          </div>
        )}

        {statements.length === 0 ? (
          <p className="text-sm text-gray-400">
            {t('games.trueFalse.addStatementsInSettings')}
          </p>
        ) : (
          <div className="space-y-3">
            {statements.map((s, idx) => (
              <div key={s.id} className="flex items-start gap-3">
                {renderBox(s.id, s.correctAnswer)}
                <p
                  className="flex-1 min-w-0"
                  style={{
                    fontSize: `${fontSize}px`,
                    fontFamily,
                    color: textColor,
                    lineHeight: 1.4,
                    paddingTop: `${Math.max(2, Math.round(fontSize * 0.25))}px`,
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {s.text || <span className="text-gray-400">{t('games.trueFalse.statementFallback', { index: idx + 1 })}</span>}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isEditor && (
        <div
          className={`flex items-center justify-end gap-2 ${
            hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
          }`}
          style={{
            backgroundColor: hideChrome ? 'transparent' : actionBarColor,
            padding: '8px 12px',
          }}
        >
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allFilled}
              className="px-4 py-1.5 text-sm font-medium rounded-lg transition text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: allFilled ? buttonColor : undefined }}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export {
  normalizeLetter as normalizeTrueFalseLetter,
  getEffectiveLetters as getEffectiveTrueFalseLetters,
};
export default TrueFalseElement;
